import React, { useRef, useEffect, useState } from 'react';
import { TouchableOpacity, Animated, View } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import TextM from '@common/components/text/TextM';
import { theme } from '@common/theme/theme';
import { StyleSheet } from 'react-native';
import { Tooltip } from "react-native-paper"
import * as amplitude from '@amplitude/analytics-react-native';

const FeedbackButton = ({
    onPress,
    disabled,
    isSelected,
    isPositive,
    label,
    tooltipText,
    minimal = false,
    iconColor = null,
    size = {
        icon: 18,
        text: 10
    }
}) => {
    const scaleAnim = useRef(new Animated.Value(1)).current;
    const opacityAnim = useRef(new Animated.Value(0)).current;
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        Animated.timing(opacityAnim, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
        }).start();
    }, []);

    const handlePressIn = () => {
        Animated.spring(scaleAnim, {
            toValue: 0.95,
            useNativeDriver: true,
        }).start();
    };

    const handlePressOut = () => {
        Animated.spring(scaleAnim, {
            toValue: 1,
            useNativeDriver: true,
        }).start();
    };
    
    const handlePress = () => {
        amplitude.track("checkpoint.feedback.button_click");
        
        onPress();
    };

    const getColors = () => {
        // Use custom iconColor if provided, otherwise use theme colors
        const defaultIconColor = isSelected ? theme.colors.darkGrey : theme.colors.grey;

        return {
            icon: iconColor || defaultIconColor,
            background: minimal ? 'transparent' : (isSelected ? `${theme.colors.grey}15` : theme.colors.lightMore),
            text: isSelected ? theme.colors.darkGrey : theme.colors.grey,
            border: minimal ? 'transparent' : (isSelected ? theme.colors.grey : 'transparent')
        };
    };

    const colors = getColors();

    return (
        <Animated.View style={{
            opacity: opacityAnim,
            transform: [{ scale: scaleAnim }]
        }}>
            <View style={styles.container}>
                <Tooltip title={tooltipText} leaveTouchDelay={500} titleMaxFontSizeMultiplier={0.5}>
                <TouchableOpacity
                    style={[
                        styles.feedbackButton,
                        minimal ? styles.minimalButton : null,
                        { 
                            backgroundColor: colors.background, 
                            borderColor: colors.border,
                            shadowColor: !minimal && isSelected ? theme.colors.grey : "#000",
                        }
                    ]}
                    onPress={handlePress}
                    onPressIn={handlePressIn}
                    onPressOut={handlePressOut}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    disabled={disabled}
                    activeOpacity={0.8}
                >
                    <FontAwesome5
                        name={isPositive ? "thumbs-up" : "thumbs-down"}
                        solid={isSelected}
                        size={size.icon}
                        color={colors.icon}
                    />
                    {!minimal && label && (
                        <TextM
                            fontSize={size.text}
                            color={colors.text}
                            style={styles.text}
                            fontWeight={isSelected ? "Bold" : "Regular"}
                        >
                            {label}
                        </TextM>
                    )}
                </TouchableOpacity>
                </Tooltip>
            </View>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'relative',
        alignItems: 'center',
    },
    feedbackButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
        backgroundColor: theme.colors.lightMore,
        borderWidth: 1,
        borderColor: 'transparent',
        elevation: 2,
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 1,
        },
        shadowOpacity: 0.1,
        shadowRadius: 2,
    },
    minimalButton: {
        paddingHorizontal: 6,
        paddingVertical: 4,
        elevation: 0,
        shadowOpacity: 0,
        borderWidth: 0,
    },
    text: {
        marginLeft: 4,
    },
    tooltip: {
        position: 'absolute',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 4,
        paddingHorizontal: 8,
        borderRadius: 4,
        top: '100%',
        marginTop: 8,
        zIndex: 1000,
    },
    tooltipArrow: {
        position: 'absolute',
        top: -4,
        left: '50%',
        marginLeft: -4,
        width: 8,
        height: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        transform: [{ rotate: '45deg' }],
    },
});

export default FeedbackButton; 